export class OfflineCache {
    // Config
    private static MAX_ITEMS = 50;
    private static MAX_SIZE_MB = 5;
    private static PREFIX = 'vintmap_cache_';

    // Calculate size in bytes/MB
    private static getSize(str: string): number {
        return new Blob([str]).size;
    }

    // Set data with TTL (default 24h)
    static set(key: string, data: any, ttlHours = 24) {
        try {
            const fullKey = this.PREFIX + key;
            const item = {
                data,
                timestamp: Date.now(),
                ttl: ttlHours * 60 * 60 * 1000
            };
            const itemStr = JSON.stringify(item);

            try {
                localStorage.setItem(fullKey, itemStr);
            } catch (e: any) {
                // If quota exceeded, prune and try again ONE time
                if (e.name === 'QuotaExceededError' || e.code === 22 ||
                    e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
                    e.message?.includes('quota')) {

                    console.log('[OfflineCache] Quota exceeded, pruning...');
                    this.pruneIfNeeded(this.getSize(itemStr));
                    try {
                        localStorage.setItem(fullKey, itemStr);
                    } catch (retryE) {
                        console.warn('[OfflineCache] Failed to save after prune', retryE);
                    }
                } else {
                    throw e;
                }
            }
        } catch (e) {
            console.warn('[OfflineCache] Failed to save', e);
        }
    }

    // Get data if valid
    static get(key: string) {
        const fullKey = this.PREFIX + key;
        const itemStr = localStorage.getItem(fullKey);
        if (!itemStr) return null;

        try {
            const { data, timestamp, ttl } = JSON.parse(itemStr);
            // Check expiry
            if (Date.now() - timestamp > ttl) {
                localStorage.removeItem(fullKey);
                return null;
            }
            return data; // Return valid data
        } catch {
            return null; // Corrupt data
        }
    }

    // Get metadata (timestamp) for UI
    static getMeta(key: string) {
        const fullKey = this.PREFIX + key;
        const itemStr = localStorage.getItem(fullKey);
        if (!itemStr) return null;
        try {
            const { timestamp } = JSON.parse(itemStr);
            return { timestamp };
        } catch {
            return null;
        }
    }

    static clear(key: string) {
        localStorage.removeItem(this.PREFIX + key);
    }

    // Smart pruning logic
    private static pruneIfNeeded(newItemSize: number) {
        let totalSize = 0;
        const cacheKeys: { key: string, time: number }[] = [];

        // Scan current usage
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(this.PREFIX)) {
                const val = localStorage.getItem(key) || '';
                totalSize += this.getSize(val);
                // Parse to find timestamp for LRU (Least Recently Used)
                try {
                    const parsed = JSON.parse(val);
                    cacheKeys.push({ key, time: parsed.timestamp || 0 });
                } catch {
                    cacheKeys.push({ key, time: 0 }); // Corrupt, remove first
                }
            }
        }

        // Limit check (Size or Count)
        const maxBytes = this.MAX_SIZE_MB * 1024 * 1024;

        if (totalSize + newItemSize > maxBytes || cacheKeys.length >= this.MAX_ITEMS) {
            // Sort oldest first
            cacheKeys.sort((a, b) => a.time - b.time);

            // Remove until we have space
            while ((totalSize + newItemSize > maxBytes || cacheKeys.length >= this.MAX_ITEMS) && cacheKeys.length > 0) {
                const toRemove = cacheKeys.shift();
                if (toRemove) {
                    const val = localStorage.getItem(toRemove.key) || '';
                    totalSize -= this.getSize(val);
                    localStorage.removeItem(toRemove.key);
                    console.log('[OfflineCache] Pruned:', toRemove.key);
                }
            }
        }
    }
}

export default OfflineCache;
